import { CommonAssortmentFields } from 'components/CommonAssortmentDialog'
import { API_HOST } from 'lib'
import { authenticatedFetch, extractErrorMessage } from 'lib/authenticatedGet'
import { CommonConfig, MaintenanceWindow } from '../domain/DTO'
import { fromInstallationScope, PathScope, StructureNode } from '../lib/InstallationScope'
import { ClaimedScopesMap } from '../components/masterdata-assignment/MasterdataAssignment'

const toMaintenanceWindowRaw = (mw: MaintenanceWindow): string => `${mw.start};${mw.end};${mw.tz};${mw.weekdays ?? 127}`

export const createCommonAssortment = async (fields: CommonAssortmentFields) => {
  return authenticatedFetch(`${API_HOST}/v1/common`, {
    method: 'POST',
    body: JSON.stringify({
      data: {
        ...fields,
        maintenanceWindow: toMaintenanceWindowRaw(fields.maintenanceWindow),
      },
    }),
  }).catch(extractErrorMessage)
}

export const duplicateCommonAssortment = async (fields: CommonAssortmentFields, duplicatedFromId: string) => {
  return authenticatedFetch(`${API_HOST}/v1/common/${duplicatedFromId}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({
      data: {
        ...fields,
        maintenanceWindow: toMaintenanceWindowRaw(fields.maintenanceWindow),
      },
    }),
  }).catch(extractErrorMessage)
}

export const updateCommonAssortment = (id: string, fields: CommonAssortmentFields) => {
  return authenticatedFetch(`${API_HOST}/v1/common/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      data: {
        ...fields,
        maintenanceWindow: toMaintenanceWindowRaw(fields.maintenanceWindow),
      },
    }),
  }).catch(extractErrorMessage)
}

export const deleteCommonAssortment = async (commonId: string) => {
  return authenticatedFetch(`${API_HOST}/v1/common/${commonId}`, {
    method: 'DELETE',
  }).catch(extractErrorMessage)
}

export function getClaimedScopesMap(
  commonAssortments: CommonConfig[],
  hierarchyData: StructureNode[],
  selectedCountry: string,
  initialScopes?: PathScope[],
): ClaimedScopesMap {
  const claimedScopes = commonAssortments
    .filter((c) => c.country === selectedCountry)
    .map((c) => [c.name, fromInstallationScope(c.installationScopes, hierarchyData)] as [string, PathScope[]])
    .filter(([_, allow]) => !!allow)
  return toClaimedScopesMapWithoutInitial(claimedScopes, initialScopes ?? [])
}

function toClaimedScopesMapWithoutInitial(scopes: [string, PathScope[]][], initial: PathScope[]): ClaimedScopesMap {
  const claimedScopesMap: ClaimedScopesMap = new Map()

  scopes.forEach(([commonName, pathScopes]) => {
    pathScopes.forEach(({ include, exclude }) => {
      include.forEach((s) => pushOrSet(claimedScopesMap, s, { name: commonName, included: true }))
      exclude.forEach((s) => pushOrSet(claimedScopesMap, s, { name: commonName, included: false }))
    })
  })

  initial.forEach(({ include, exclude }) => {
    include.forEach((includedPath) => {
      const includedPathValues = claimedScopesMap.get(includedPath)
      if ((includedPathValues?.length ?? 0) > 1) {
        claimedScopesMap.set(
          includedPath,
          includedPathValues?.filter((claimedCommon) => {
            return !claimedCommon.included
          }),
        )
      } else {
        claimedScopesMap.delete(includedPath)
      }
    })
    exclude.forEach((excludedPath) => {
      const excludedPathValues = claimedScopesMap.get(excludedPath)
      if ((excludedPathValues?.length ?? 0) > 1) {
        claimedScopesMap.set(
          excludedPath,
          excludedPathValues?.filter((claimedCommon) => {
            return claimedCommon.included
          }),
        )
      } else {
        claimedScopesMap.delete(excludedPath)
      }
    })
  })
  return claimedScopesMap
}

/**
 * Pushes a value to an existing key or creates a new entry if the key does not exist.
 */
function pushOrSet<K, V>(map: Map<K, Array<V> | undefined>, key: K, valueToAdd: V): void {
  const exiting = map.get(key)
  if (exiting) {
    exiting?.push(valueToAdd)
  } else {
    map.set(key, [valueToAdd])
  }
}
